import React, { useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  styled,
  DialogContentText,
  Typography,
} from '@mui/material';
import { useDropzone } from 'react-dropzone'
import axios from 'axios';
import moment from 'moment';

function parseInput(input) {
  const delimiter_row = /\r?\n|\r|\n/g
  const delimiter_col = /[,\t]/
  const rows = input.split(delimiter_row)
  // Check if first row has at least 3 expected headers
  const firstRow = rows[0].toLowerCase()
  const hasName = firstRow.includes("name")
  const hasDescription = firstRow.includes("description")
  const hasPassage = firstRow.includes("passage")
  const hasDate = firstRow.includes("date")
  const hasCells = firstRow.includes("cells")
  const hasVolume = firstRow.includes("volume")
  const hasHeader = (hasName && hasDescription && hasPassage) || (hasName && hasCells && hasVolume)
  const header = hasHeader ? rows.shift().split(delimiter_col) /* remove first row */ : ["Name", "Description", "Passage", "Date", "Cells", "Volume"]
  const headerIndName = header.indexOf("Name")
  const headerIndDescription = header.indexOf("Description")
  const headerIndPassage = header.indexOf("Passage")
  const headerIndDate = header.indexOf("Date")
  const headerIndCells = header.indexOf("Cells")
  const headerIndVolume = header.indexOf("Volume")
  const parseRow = (row) => {
    const array = row.split(delimiter_col)
    return {
      name: headerIndName >= 0 && array[headerIndName],
      description: headerIndDescription >= 0 && array[headerIndDescription],
      passage: headerIndPassage >= 0 && array[headerIndPassage],
      date: headerIndDate >= 0 && array[headerIndDate],
      cells: headerIndCells >= 0 && array[headerIndCells],
      volume: headerIndVolume >= 0 && array[headerIndVolume],
    }
  }
  return rows.filter(row => row !== "").map(row => parseRow(row))
}

export default function ImportCryostocksForm({open, onClose, onSubmit, showMessage, setData, ...props}) {
  const [input, setInput] = useState()
  const parsed = input ? parseInput(input) : []
  const handleSubmit = (event) => {
    event.preventDefault()
    onSubmit?.(event)
    onClose?.()
    // prepare data for API - convert volume to nL and date to ISO format
    const formattedData = parsed.map(stock => ({
      ...stock,
      volume: stock.volume ? parseFloat(stock.volume) * 1000 : undefined, // convert μL to nL
      cells: stock.cells ? parseFloat(stock.cells) : undefined,
      passage: stock.passage ? parseInt(stock.passage) : undefined,
      date: stock.date ? moment(stock.date).toISOString() : undefined,
    }))
    // post to server
    axios.post("/api/wetlab/tc_basic/cryobank/add", {add: formattedData})
      .then(({data : {ids}}) => showMessage?.(`Saved ${ids.length} cryostock${ids.length === 1 ? '' : 's'}`))
      .catch(() => showMessage?.("Error saving cryostocks"))
    // post to locally
    setData?.(prev => [...prev, ...formattedData.map((stock, i) => ({...stock, id: -i}))])
  }
  const TableCellNoWrap = styled(TableCell)({
    whiteSpace: "nowrap",
  })
  const onDrop = useCallback(acceptedFiles => {
    const reader = new FileReader();
    reader.onerror = () => showMessage?.("Error reading file.")
    reader.onload = (event) => setInput(event.target.result)
    acceptedFiles.forEach(file => {
      if (file.type.split("/")[0] !== "text") {
        showMessage(`Can't parse files of type ${file.type}`)
        return
      }
      reader.readAsText(file)
    });
  // because showMessage deps
  // eslint-disable-next-line
  }, [])
  const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop: onDrop, noClick: true, noKeyboard: true})
  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="lg" {...props}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>New Batch of Cryostocks</DialogTitle>
        <DialogContent>
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <TextField
              margin="dense"
              placeholder="Rows of Name, Description, Passage, Date, Cells, and Volume (μL)"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              variant="outlined"
              value={input}
              onChange={e => setInput(e.target.value)}
              sx={{whiteSpace: "nowrap"}}
              focused={isDragActive || undefined} // force focus color or inherit focus state
            />
          </div>
          {parsed.length > 0 && <TableContainer><Table size="small">
            <TableHead>
              <TableRow>
                <TableCellNoWrap>Name</TableCellNoWrap>
                <TableCellNoWrap>Description</TableCellNoWrap>
                <TableCellNoWrap>Passage</TableCellNoWrap>
                <TableCellNoWrap>Date</TableCellNoWrap>
                <TableCellNoWrap>Cells</TableCellNoWrap>
                <TableCellNoWrap>Volume (μL)</TableCellNoWrap>
              </TableRow>
            </TableHead>
            <TableBody>
              {parsed.map((row, i) => 
                <TableRow key={i}>
                  <TableCellNoWrap>{row.name}</TableCellNoWrap>
                  <TableCellNoWrap>{row.description}</TableCellNoWrap>
                  <TableCellNoWrap>{row.passage}</TableCellNoWrap>
                  <TableCellNoWrap>{row.date}</TableCellNoWrap>
                  <TableCellNoWrap>{row.cells}</TableCellNoWrap>
                  <TableCellNoWrap>{row.volume}</TableCellNoWrap>
                </TableRow>
              )}
            </TableBody>
          </Table></TableContainer>}
          <DialogContentText>
            <Typography variant="caption">
              {parsed.length} cryostock{parsed.length === 1 ? '' : 's'}
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit">Add</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
